import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import logger from '../lib/logger';
import { useTranslation } from '../i18n/LanguageProvider';

interface PublicRouteProps {
  children: React.ReactNode;
  restrictedPath?: string; // Vart ska inloggade användare omdirigeras?
}

/**
 * PublicRoute - För publika routes (t.ex. /login)
 * 
 * Funktioner:
 * - Autentiserade användare omdirigeras bort från publika routes
 * - Oautentiserade användare får se innehållet
 */
const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  restrictedPath = '/customers', // Default: inloggade användare går till /customers
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('common.loading') || 'Laddar...'}
          </p>
        </div>
      </div>
    );
  }

  // Om användaren är inloggad, omdirigera bort från publik route
  if (isAuthenticated) {
    const from = (location.state as any)?.from || restrictedPath;
    
    logger.audit('Inloggad användare omdirigeras från publik route', { 
      from: location.pathname,
      to: from,
    });
    
    return <Navigate to={from} replace />;
  }

  // Användaren är inte inloggad, visa publikt innehåll
  return <>{children}</>;
};

export default PublicRoute;
